import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DropdownService, MyOwnActionServiceDto, RecipientServiceDto, ServiceAccessRightsDto, ServiceDto } from '../interfaces';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { ServiceParams } from '../interfaces/services/service-params.interface';
import { environment } from 'src/environments/environment';
import { DocumentFusionFieldsDTO } from '../models/iCourrier/document-template';
import { setServiceDescription } from 'src/app/utils/set-service-description.utils';

@Injectable({
  providedIn: 'root',
})
export class ServicesService {
  private iAdminURL;
  private iCourrierURL;
  public headers: HttpHeaders;
  public params: HttpParams = new HttpParams();
  private mappedServices$: Observable<DropdownService[]>;

  constructor(private http: HttpClient) {
    const config = environment;
    this.iAdminURL = config.iAdminApiBaseURL;
    this.iCourrierURL = config.apiICourrierBaseUrl;
  }

  public fetchServices(): Observable<ServiceDto[]> {
    return this.http.get<ServiceDto[]>(`${this.iAdminURL}/Global/Services`);
  }

  /**
   * Fetches the services mapped for PrimeNG dropdowns. The unfiltered list (no `params`) is cached for the app
   * lifetime (it rarely changes during a session); a call filtered by `params` always hits the API.
   */
  public fetchMappedServicesToPrimeNGDropdowns(params?: Partial<ServiceParams>): Observable<DropdownService[]> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => (httpParams = httpParams.set(key, params[key])));
    }
    const mapServices = map((res: ServiceDto[]) => {
      const mappedServicesArray: DropdownService[] = [];

      res.forEach((service: ServiceDto) => {
        let mappedService: DropdownService = {} as DropdownService;
        mappedService = {
          ...service,
          label: setServiceDescription(service),
          description: setServiceDescription(service),
          name: setServiceDescription(service),
          value: service.id,
        };
        mappedServicesArray.push(mappedService);
      });

      return mappedServicesArray;
    });

    if (params) {
      return this.http.get<ServiceDto[]>(`${this.iAdminURL}/Global/Services?${httpParams}`).pipe(mapServices);
    }
    if (!this.mappedServices$) {
      this.mappedServices$ = this.http.get<ServiceDto[]>(`${this.iAdminURL}/Global/Services?${httpParams}`).pipe(mapServices, shareReplay(1));
    }
    return this.mappedServices$;
  }

  public fetchServiceUsersIds(serviceId: number): Observable<number[]> {
    return this.http.get<number[]>(`${this.iCourrierURL}/Global/ServiceDetails/${serviceId}/Users/Ids`);
  }

  public fetchServiceAccessRights(serviceId: number): Observable<ServiceAccessRightsDto> {
    return this.http.get<ServiceAccessRightsDto>(`${this.iCourrierURL}/Management/ServiceDetails/${serviceId}`);
  }

  public fetchMyOwnActionServices(): Observable<MyOwnActionServiceDto> {
    return this.http.get<MyOwnActionServiceDto>(`${this.iCourrierURL}/Global/Actions/MyServiceDetails`);
  }

  public fetchRecipientServices(userId: number): Observable<RecipientServiceDto> {
    return this.http.get<RecipientServiceDto>(`${this.iCourrierURL}/Global/Actions/DestinataireServiceDetails`, {
      params: { userId: userId },
    });
  }

  public fetchSignature(serviceId: number): Observable<{
    id: number;
    eMailSignatureRTF: string;
  }> {
    return this.http.get<{
      id: number;
      eMailSignatureRTF: string;
    }>(`${this.iCourrierURL}/Global/Services/${serviceId}/MailSignatureFusionee`);
  }

  public getFusionsFieldServices(): Observable<DocumentFusionFieldsDTO> {
    return this.http.get<DocumentFusionFieldsDTO>(`${this.iCourrierURL}/Global/Services/Templates/FusionChamps`);
  }
}
